import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import loginIcon from "./assets/login.png";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Navigation from "./RootNavigation";
import Footer from "./Footer";

export default function Login() {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

const searchParams = new URLSearchParams(location.search);

const returnTo = searchParams.get("returnTo");

  //  On mount: restore saved username if any
  useEffect(() => {
    const saved = localStorage.getItem("rememberedUsername");
    if (saved) {
      setCredentials((prev) => ({ ...prev, username: saved }));
      setRememberMe(true);
    }
  }, []);

useEffect(() => {
  const userRole = localStorage.getItem("userRole");

  if (!userRole) return;

  if (returnTo && userRole === "USER") {
    navigate(returnTo);
    return;
  }

  switch (userRole) {
    case "USER":
      navigate("/myresume");
      break;

    case "COMPANY":
      navigate("/dashboard");
      break;

    default:
      navigate("/admin-dashboard");
  }
}, [navigate, returnTo]);

  //  Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await api.get(ENDPOINTS.authenticate(), {
        params: {
          UserName: credentials.username,
          Password: credentials.password,
        },
      });

      const res = response.data;

      if (res?.httpStatusCode === 200) {
        //  Save login details
        localStorage.setItem("loggedInUser", JSON.stringify(res));
        localStorage.setItem("userId", res.data.userId);
        localStorage.setItem("email", res.data.email);
        localStorage.setItem("userRole", res.data.userRole);
        localStorage.setItem(
  "userName",
    res.data.userName ||
    credentials.username
);

        //  Save remembered username if checkbox is checked
        if (rememberMe) {
          localStorage.setItem("rememberedUsername", credentials.username);
        } else {
          localStorage.removeItem("rememberedUsername");
        }

        //  Redirect based on role
        switch (res.data.userRole) {
          case "COMPANY":
            navigate("/dashboard");
            break;
          case "USER":
  if (returnTo) {
    navigate(returnTo);
  } else {
    navigate("/onboarding");
  }
  break;
          case "ADMIN":
            navigate("/admin-dashboard");
            break;
          default:
            navigate("/");
        }
      } else {
        setError(res.message || "Invalid username or password.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Unable to connect. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navigation />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Grid
          container
          sx={{
            maxWidth: 1200,
            backgroundColor: "#fff",
            overflow: "hidden",
            alignItems: "stretch",
          }}
        >
          {/* LEFT IMAGE */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              order: { xs: 2, md: 1 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
            }}
          >
            <Box
              component="img"
              src={loginIcon}
              alt="login visual"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          </Grid>

          {/* FORM */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              px: { xs: 4, md: 6 },
              pb: { xs: 4, md: 6 },
              pt: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              order: { xs: 1, md: 2 },
            }}
          >
            <Typography
              textAlign="center"
              sx={{ mb: 3, lineHeight: 1.7, color: "text.secondary" }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: 600, display: "block", mb: 1, color: "text.primary" }}
              >
                Existing NYCastings Members:
              </Typography>
              Use your current{" "}
              <Typography
                component="a"
                href="https://www.nycastings.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#90b5fe", fontWeight: 500, textDecoration: "none" }}
              >
                NYCastings.com
              </Typography>{" "}
              username and password to log in, then click{" "}
              <Typography component="strong" sx={{ fontWeight: 600 }}>
                Remember Me
              </Typography>{" "}
              to stay logged in. If you&rsquo;ve forgotten your login
              credentials, use{" "}
              <Typography component="strong" sx={{ fontWeight: 600 }}>
                Forgot Username
              </Typography>{" "}
              or{" "}
              <Typography component="strong" sx={{ fontWeight: 600 }}>
                Forgot Password
              </Typography>{" "}
              below.
            </Typography>

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                required
                margin="normal"
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={credentials.password}
                onChange={handleChange}
                required
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me"
                />
              </Box>

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}

              {/* LOGIN BUTTON */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  backgroundColor: "#90b5fe",
                  "&:hover": {
                    backgroundColor: "#202020",
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Login"}
              </Button>

              {/* LINKS */}
              <Typography textAlign="center" sx={{ mt: 2 }}>
                Not a member?{" "}
                <Typography
                  component="span"
                  sx={{
                    color: "#90b5fe",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Sign up
                </Typography>
              </Typography>

              <Typography textAlign="center" sx={{ mt: 1 }}>
                Forgot Password?{" "}
                <Typography
                  component="span"
                  sx={{
                    color: "#90b5fe",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                  onClick={() => navigate("/forgot-password")}
                >
                  Retrieve Now
                </Typography>
              </Typography>
              <Typography textAlign="center" sx={{ mt: 1 }}>
  Forgot Username?{" "}
  <Typography
    component="span"
    sx={{
      color: "#90b5fe",
      cursor: "pointer",
      fontWeight: 500,
    }}
    onClick={() => navigate("/forgot-username")}
  >
    Retrieve Now
  </Typography>
</Typography>
            </form>
          </Grid>
        </Grid>
      </Box>

      <Footer />
    </>
  );
}